import { useState } from "react";
import dynamic from "next/dynamic";
import Theme from "/components/Theme";
const People = dynamic(() => import("/components/organisms/common/People"));
const WideCTA = dynamic(() => import("/components/organisms/common/WideCTA"));
const AdaptiveCriticalImage = dynamic(() =>
  import("@components/atoms/AdaptiveCriticalImage")
);
const FAQ = dynamic(() => import("@components/organisms/common/FAQ"));
const Schema = dynamic(() =>
  import("../../components/organisms/common/Schema")
);
const ResponsiveImage = dynamic(() =>
  import("@components/atoms/image/ResponsiveImage")
);
import AppStorage from "../../components/AppStorage";
import FeaturesGridWithLink from "../../components/organisms/common/FeaturesGridWithLink";

export async function getStaticProps(ctx) {
  const { locale } = ctx;
  let allData = {};

  // Fetch
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );
  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );
  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/features/qr-code-appointment-booking.json`
  );

  // Catch Error
  if (contentResponse.status === 404) {
    return {
      notFound: true,
    };
  }

  // Convert
  allData.header = await headerResponse.json();
  allData.content = await contentResponse.json();
  allData.footer = await footerResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };

  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...allData.content,
  };

  return {
    props: {
      data,
    },
  };
}
export default function QRCodeAppointmentBooking({ data, nonce }) {
  let [slide, setSlide] = useState(1);
  const seo = data.page_meta;
  const content = data.page_content;
  const header = data.header;
  const footer = data.footer;
  setSlide = (slideNo) => {
    slide = slideNo;
    const parent = document.getElementById("image-slides").children;
    const child = parent.item(slideNo - 1);
    const previousChild = document.querySelector("#image-slides .active");
    previousChild.classList.toggle("active");
    child.classList.toggle("active");
    const slideShow = document.querySelector(".slideshow-container").children;
    const currentSlide = slideShow.item(slideNo - 1);
    const previousSlide = document.querySelector(
      ".slideshow-container .d-block"
    );
    previousSlide.classList.toggle("d-block");
    currentSlide.classList.toggle("d-block");
  };
  const Hero = ({ data }) => {
    return (
      <section className="container grid--default has-align-top hero--default lg:mb-11 md:pb-10">
        <article className="text-left lg:maxw-lg md:pb-5">
          <h1 className="text-xl lg:pt-5">{data.title}</h1>
          <p
            className="lg:maxw-md mt-4 mb-5 md:mt-2.5 md:mb-2.5"
            dangerouslySetInnerHTML={{ __html: data.desc[0] }}
          />
          <a
            className="btn-primary lg:pl-12.5 lg:pr-12.5 md:w-100"
            href={data.cta.href}
          >
            <span>{data.cta.text}</span>
          </a>
        </article>
        <figure>
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes.mobile}
              dpi={data.img.prefixes.dpi}
              className="imgholder-primary lg:ml-3 lg:mt-2 md:w-100 img-fit"
              dimensions={data.img.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };
  const CTA = ({ data }) => {
    const Description = ({ data }) => {
      return data.map((item, count) => {
        return (
          <p
            className="lg:mt-1.5 md:mt-1.5"
            dangerouslySetInnerHTML={{ __html: item }}
            key={count}
          />
        );
      });
    };
    return (
      <section className="bg-offset-bottom">
        <div className="container cta-contrast has-image has-img-offset br-8 lg:d-fx lg:mt-20 md:mt-10 lg:pt-10 lg:pb-10">
          <article className="lg:maxw-md lg:mr-5">
            <h2> {data.heading} </h2>
            <Description data={data.desc} />
          </article>
          <figure className="lg:ml-12.5 lg:pl-8 md:d-none">
            <svg
              width="320"
              height="320"
              viewBox="0 0 320 320"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M40 40H49.6V49.6H40V40ZM49.6 40H59.2V49.6H49.6V40ZM59.2 40H68.8V49.6H59.2V40ZM68.8 40H78.4V49.6H68.8V40ZM78.4 40H88V49.6H78.4V40ZM88 40H97.6V49.6H88V40ZM97.6 40H107.2V49.6H97.6V40ZM126.4 40H136V49.6H126.4V40ZM174.4 40H184V49.6H174.4V40ZM184 40H193.6V49.6H184V40ZM193.6 40H203.2V49.6H193.6V40ZM212.8 40H222.4V49.6H212.8V40ZM222.4 40H232V49.6H222.4V40ZM232 40H241.6V49.6H232V40ZM241.6 40H251.2V49.6H241.6V40ZM251.2 40H260.8V49.6H251.2V40ZM260.8 40H270.4V49.6H260.8V40ZM270.4 40H280V49.6H270.4V40ZM40 49.6H49.6V59.2H40V49.6ZM97.6 49.6H107.2V59.2H97.6V49.6ZM116.8 49.6H126.4V59.2H116.8V49.6ZM126.4 49.6H136V59.2H126.4V49.6ZM145.6 49.6H155.2V59.2H145.6V49.6ZM155.2 49.6H164.8V59.2H155.2V49.6ZM164.8 49.6H174.4V59.2H164.8V49.6ZM174.4 49.6H184V59.2H174.4V49.6ZM184 49.6H193.6V59.2H184V49.6ZM212.8 49.6H222.4V59.2H212.8V49.6ZM270.4 49.6H280V59.2H270.4V49.6ZM40 59.2H49.6V68.8H40V59.2ZM59.2 59.2H68.8V68.8H59.2V59.2ZM68.8 59.2H78.4V68.8H68.8V59.2ZM78.4 59.2H88V68.8H78.4V59.2ZM97.6 59.2H107.2V68.8H97.6V59.2ZM126.4 59.2H136V68.8H126.4V59.2ZM136 59.2H145.6V68.8H136V59.2ZM145.6 59.2H155.2V68.8H145.6V59.2ZM184 59.2H193.6V68.8H184V59.2ZM193.6 59.2H203.2V68.8H193.6V59.2ZM212.8 59.2H222.4V68.8H212.8V59.2ZM232 59.2H241.6V68.8H232V59.2ZM241.6 59.2H251.2V68.8H241.6V59.2ZM251.2 59.2H260.8V68.8H251.2V59.2ZM270.4 59.2H280V68.8H270.4V59.2ZM40 68.8H49.6V78.4H40V68.8ZM59.2 68.8H68.8V78.4H59.2V68.8ZM68.8 68.8H78.4V78.4H68.8V68.8ZM78.4 68.8H88V78.4H78.4V68.8ZM97.6 68.8H107.2V78.4H97.6V68.8ZM116.8 68.8H126.4V78.4H116.8V68.8ZM126.4 68.8H136V78.4H126.4V68.8ZM145.6 68.8H155.2V78.4H145.6V68.8ZM164.8 68.8H174.4V78.4H164.8V68.8ZM174.4 68.8H184V78.4H174.4V68.8ZM184 68.8H193.6V78.4H184V68.8ZM193.6 68.8H203.2V78.4H193.6V68.8ZM212.8 68.8H222.4V78.4H212.8V68.8ZM232 68.8H241.6V78.4H232V68.8ZM241.6 68.8H251.2V78.4H241.6V68.8ZM251.2 68.8H260.8V78.4H251.2V68.8ZM270.4 68.8H280V78.4H270.4V68.8ZM40 78.4H49.6V88H40V78.4ZM59.2 78.4H68.8V88H59.2V78.4ZM68.8 78.4H78.4V88H68.8V78.4ZM78.4 78.4H88V88H78.4V78.4ZM97.6 78.4H107.2V88H97.6V78.4ZM136 78.4H145.6V88H136V78.4ZM155.2 78.4H164.8V88H155.2V78.4ZM164.8 78.4H174.4V88H164.8V78.4ZM184 78.4H193.6V88H184V78.4ZM193.6 78.4H203.2V88H193.6V78.4ZM212.8 78.4H222.4V88H212.8V78.4ZM232 78.4H241.6V88H232V78.4ZM241.6 78.4H251.2V88H241.6V78.4ZM251.2 78.4H260.8V88H251.2V78.4ZM270.4 78.4H280V88H270.4V78.4ZM40 88H49.6V97.6H40V88ZM97.6 88H107.2V97.6H97.6V88ZM116.8 88H126.4V97.6H116.8V88ZM136 88H145.6V97.6H136V88ZM164.8 88H174.4V97.6H164.8V88ZM174.4 88H184V97.6H174.4V88ZM184 88H193.6V97.6H184V88ZM212.8 88H222.4V97.6H212.8V88ZM270.4 88H280V97.6H270.4V88ZM40 97.6H49.6V107.2H40V97.6ZM49.6 97.6H59.2V107.2H49.6V97.6ZM59.2 97.6H68.8V107.2H59.2V97.6ZM68.8 97.6H78.4V107.2H68.8V97.6ZM78.4 97.6H88V107.2H78.4V97.6ZM88 97.6H97.6V107.2H88V97.6ZM97.6 97.6H107.2V107.2H97.6V97.6ZM116.8 97.6H126.4V107.2H116.8V97.6ZM136 97.6H145.6V107.2H136V97.6ZM155.2 97.6H164.8V107.2H155.2V97.6ZM174.4 97.6H184V107.2H174.4V97.6ZM193.6 97.6H203.2V107.2H193.6V97.6ZM212.8 97.6H222.4V107.2H212.8V97.6ZM222.4 97.6H232V107.2H222.4V97.6ZM232 97.6H241.6V107.2H232V97.6ZM241.6 97.6H251.2V107.2H241.6V97.6ZM251.2 97.6H260.8V107.2H251.2V97.6ZM260.8 97.6H270.4V107.2H260.8V97.6ZM270.4 97.6H280V107.2H270.4V97.6ZM155.2 107.2H164.8V116.8H155.2V107.2ZM174.4 107.2H184V116.8H174.4V107.2ZM40 116.8H49.6V126.4H40V116.8ZM49.6 116.8H59.2V126.4H49.6V116.8ZM59.2 116.8H68.8V126.4H59.2V116.8ZM68.8 116.8H78.4V126.4H68.8V116.8ZM78.4 116.8H88V126.4H78.4V116.8ZM97.6 116.8H107.2V126.4H97.6V116.8ZM107.2 116.8H116.8V126.4H107.2V116.8ZM116.8 116.8H126.4V126.4H116.8V116.8ZM126.4 116.8H136V126.4H126.4V116.8ZM136 116.8H145.6V126.4H136V116.8ZM155.2 116.8H164.8V126.4H155.2V116.8ZM164.8 116.8H174.4V126.4H164.8V116.8ZM174.4 116.8H184V126.4H174.4V116.8ZM184 116.8H193.6V126.4H184V116.8ZM193.6 116.8H203.2V126.4H193.6V116.8ZM203.2 116.8H212.8V126.4H203.2V116.8ZM222.4 116.8H232V126.4H222.4V116.8ZM241.6 116.8H251.2V126.4H241.6V116.8ZM260.8 116.8H270.4V126.4H260.8V116.8ZM40 126.4H49.6V136H40V126.4ZM49.6 126.4H59.2V136H49.6V126.4ZM59.2 126.4H68.8V136H59.2V126.4ZM68.8 126.4H78.4V136H68.8V126.4ZM88 126.4H97.6V136H88V126.4ZM116.8 126.4H126.4V136H116.8V126.4ZM126.4 126.4H136V136H126.4V126.4ZM164.8 126.4H174.4V136H164.8V126.4ZM222.4 126.4H232V136H222.4V126.4ZM260.8 126.4H270.4V136H260.8V126.4ZM40 136H49.6V145.6H40V136ZM68.8 136H78.4V145.6H68.8V136ZM78.4 136H88V145.6H78.4V136ZM97.6 136H107.2V145.6H97.6V136ZM107.2 136H116.8V145.6H107.2V136ZM116.8 136H126.4V145.6H116.8V136ZM126.4 136H136V145.6H126.4V136ZM145.6 136H155.2V145.6H145.6V136ZM155.2 136H164.8V145.6H155.2V136ZM164.8 136H174.4V145.6H164.8V136ZM174.4 136H184V145.6H174.4V136ZM184 136H193.6V145.6H184V136ZM193.6 136H203.2V145.6H193.6V136ZM232 136H241.6V145.6H232V136ZM241.6 136H251.2V145.6H241.6V136ZM260.8 136H270.4V145.6H260.8V136ZM270.4 136H280V145.6H270.4V136ZM49.6 145.6H59.2V155.2H49.6V145.6ZM59.2 145.6H68.8V155.2H59.2V145.6ZM88 145.6H97.6V155.2H88V145.6ZM126.4 145.6H136V155.2H126.4V145.6ZM136 145.6H145.6V155.2H136V145.6ZM145.6 145.6H155.2V155.2H145.6V145.6ZM193.6 145.6H203.2V155.2H193.6V145.6ZM203.2 145.6H212.8V155.2H203.2V145.6ZM212.8 145.6H222.4V155.2H212.8V145.6ZM270.4 145.6H280V155.2H270.4V145.6ZM40 155.2H49.6V164.8H40V155.2ZM59.2 155.2H68.8V164.8H59.2V155.2ZM78.4 155.2H88V164.8H78.4V155.2ZM97.6 155.2H107.2V164.8H97.6V155.2ZM116.8 155.2H126.4V164.8H116.8V155.2ZM126.4 155.2H136V164.8H126.4V155.2ZM145.6 155.2H155.2V164.8H145.6V155.2ZM174.4 155.2H184V164.8H174.4V155.2ZM203.2 155.2H212.8V164.8H203.2V155.2ZM212.8 155.2H222.4V164.8H212.8V155.2ZM232 155.2H241.6V164.8H232V155.2ZM251.2 155.2H260.8V164.8H251.2V155.2ZM260.8 155.2H270.4V164.8H260.8V155.2ZM270.4 155.2H280V164.8H270.4V155.2ZM40 164.8H49.6V174.4H40V164.8ZM49.6 164.8H59.2V174.4H49.6V164.8ZM126.4 164.8H136V174.4H126.4V164.8ZM136 164.8H145.6V174.4H136V164.8ZM155.2 164.8H164.8V174.4H155.2V164.8ZM174.4 164.8H184V174.4H174.4V164.8ZM203.2 164.8H212.8V174.4H203.2V164.8ZM222.4 164.8H232V174.4H222.4V164.8ZM241.6 164.8H251.2V174.4H241.6V164.8ZM260.8 164.8H270.4V174.4H260.8V164.8ZM40 174.4H49.6V184H40V174.4ZM59.2 174.4H68.8V184H59.2V174.4ZM68.8 174.4H78.4V184H68.8V174.4ZM88 174.4H97.6V184H88V174.4ZM97.6 174.4H107.2V184H97.6V174.4ZM164.8 174.4H174.4V184H164.8V174.4ZM174.4 174.4H184V184H174.4V174.4ZM184 174.4H193.6V184H184V174.4ZM193.6 174.4H203.2V184H193.6V174.4ZM203.2 174.4H212.8V184H203.2V174.4ZM212.8 174.4H222.4V184H212.8V174.4ZM222.4 174.4H232V184H222.4V174.4ZM232 174.4H241.6V184H232V174.4ZM241.6 174.4H251.2V184H241.6V174.4ZM260.8 174.4H270.4V184H260.8V174.4ZM270.4 174.4H280V184H270.4V174.4ZM40 184H49.6V193.6H40V184ZM68.8 184H78.4V193.6H68.8V184ZM107.2 184H116.8V193.6H107.2V184ZM116.8 184H126.4V193.6H116.8V184ZM126.4 184H136V193.6H126.4V184ZM155.2 184H164.8V193.6H155.2V184ZM184 184H193.6V193.6H184V184ZM212.8 184H222.4V193.6H212.8V184ZM222.4 184H232V193.6H222.4V184ZM232 184H241.6V193.6H232V184ZM270.4 184H280V193.6H270.4V184ZM40 193.6H49.6V203.2H40V193.6ZM68.8 193.6H78.4V203.2H68.8V193.6ZM97.6 193.6H107.2V203.2H97.6V193.6ZM107.2 193.6H116.8V203.2H107.2V193.6ZM126.4 193.6H136V203.2H126.4V193.6ZM155.2 193.6H164.8V203.2H155.2V193.6ZM164.8 193.6H174.4V203.2H164.8V193.6ZM193.6 193.6H203.2V203.2H193.6V193.6ZM203.2 193.6H212.8V203.2H203.2V193.6ZM212.8 193.6H222.4V203.2H212.8V193.6ZM222.4 193.6H232V203.2H222.4V193.6ZM232 193.6H241.6V203.2H232V193.6ZM251.2 193.6H260.8V203.2H251.2V193.6ZM116.8 203.2H126.4V212.8H116.8V203.2ZM164.8 203.2H174.4V212.8H164.8V203.2ZM174.4 203.2H184V212.8H174.4V203.2ZM193.6 203.2H203.2V212.8H193.6V203.2ZM232 203.2H241.6V212.8H232V203.2ZM241.6 203.2H251.2V212.8H241.6V203.2ZM40 212.8H49.6V222.4H40V212.8ZM49.6 212.8H59.2V222.4H49.6V212.8ZM59.2 212.8H68.8V222.4H59.2V212.8ZM68.8 212.8H78.4V222.4H68.8V212.8ZM78.4 212.8H88V222.4H78.4V212.8ZM88 212.8H97.6V222.4H88V212.8ZM97.6 212.8H107.2V222.4H97.6V212.8ZM116.8 212.8H126.4V222.4H116.8V212.8ZM126.4 212.8H136V222.4H126.4V212.8ZM145.6 212.8H155.2V222.4H145.6V212.8ZM155.2 212.8H164.8V222.4H155.2V212.8ZM193.6 212.8H203.2V222.4H193.6V212.8ZM212.8 212.8H222.4V222.4H212.8V212.8ZM232 212.8H241.6V222.4H232V212.8ZM251.2 212.8H260.8V222.4H251.2V212.8ZM260.8 212.8H270.4V222.4H260.8V212.8ZM270.4 212.8H280V222.4H270.4V212.8ZM40 222.4H49.6V232H40V222.4ZM97.6 222.4H107.2V232H97.6V222.4ZM126.4 222.4H136V232H126.4V222.4ZM145.6 222.4H155.2V232H145.6V222.4ZM193.6 222.4H203.2V232H193.6V222.4ZM232 222.4H241.6V232H232V222.4ZM241.6 222.4H251.2V232H241.6V222.4ZM40 232H49.6V241.6H40V232ZM59.2 232H68.8V241.6H59.2V232ZM68.8 232H78.4V241.6H68.8V232ZM78.4 232H88V241.6H78.4V232ZM97.6 232H107.2V241.6H97.6V232ZM116.8 232H126.4V241.6H116.8V232ZM126.4 232H136V241.6H126.4V232ZM145.6 232H155.2V241.6H145.6V232ZM184 232H193.6V241.6H184V232ZM193.6 232H203.2V241.6H193.6V232ZM203.2 232H212.8V241.6H203.2V232ZM212.8 232H222.4V241.6H212.8V232ZM222.4 232H232V241.6H222.4V232ZM232 232H241.6V241.6H232V232ZM251.2 232H260.8V241.6H251.2V232ZM260.8 232H270.4V241.6H260.8V232ZM270.4 232H280V241.6H270.4V232ZM40 241.6H49.6V251.2H40V241.6ZM59.2 241.6H68.8V251.2H59.2V241.6ZM68.8 241.6H78.4V251.2H68.8V241.6ZM78.4 241.6H88V251.2H78.4V241.6ZM97.6 241.6H107.2V251.2H97.6V241.6ZM116.8 241.6H126.4V251.2H116.8V241.6ZM126.4 241.6H136V251.2H126.4V241.6ZM136 241.6H145.6V251.2H136V241.6ZM155.2 241.6H164.8V251.2H155.2V241.6ZM164.8 241.6H174.4V251.2H164.8V241.6ZM184 241.6H193.6V251.2H184V241.6ZM212.8 241.6H222.4V251.2H212.8V241.6ZM232 241.6H241.6V251.2H232V241.6ZM241.6 241.6H251.2V251.2H241.6V241.6ZM251.2 241.6H260.8V251.2H251.2V241.6ZM260.8 241.6H270.4V251.2H260.8V241.6ZM270.4 241.6H280V251.2H270.4V241.6ZM40 251.2H49.6V260.8H40V251.2ZM59.2 251.2H68.8V260.8H59.2V251.2ZM68.8 251.2H78.4V260.8H68.8V251.2ZM78.4 251.2H88V260.8H78.4V251.2ZM97.6 251.2H107.2V260.8H97.6V251.2ZM116.8 251.2H126.4V260.8H116.8V251.2ZM126.4 251.2H136V260.8H126.4V251.2ZM136 251.2H145.6V260.8H136V251.2ZM164.8 251.2H174.4V260.8H164.8V251.2ZM174.4 251.2H184V260.8H174.4V251.2ZM193.6 251.2H203.2V260.8H193.6V251.2ZM241.6 251.2H251.2V260.8H241.6V251.2ZM251.2 251.2H260.8V260.8H251.2V251.2ZM270.4 251.2H280V260.8H270.4V251.2ZM40 260.8H49.6V270.4H40V260.8ZM97.6 260.8H107.2V270.4H97.6V260.8ZM116.8 260.8H126.4V270.4H116.8V260.8ZM126.4 260.8H136V270.4H126.4V260.8ZM136 260.8H145.6V270.4H136V260.8ZM155.2 260.8H164.8V270.4H155.2V260.8ZM164.8 260.8H174.4V270.4H164.8V260.8ZM184 260.8H193.6V270.4H184V260.8ZM193.6 260.8H203.2V270.4H193.6V260.8ZM203.2 260.8H212.8V270.4H203.2V260.8ZM222.4 260.8H232V270.4H222.4V260.8ZM232 260.8H241.6V270.4H232V260.8ZM241.6 260.8H251.2V270.4H241.6V260.8ZM270.4 260.8H280V270.4H270.4V260.8ZM40 270.4H49.6V280H40V270.4ZM49.6 270.4H59.2V280H49.6V270.4ZM59.2 270.4H68.8V280H59.2V270.4ZM68.8 270.4H78.4V280H68.8V270.4ZM78.4 270.4H88V280H78.4V270.4ZM88 270.4H97.6V280H88V270.4ZM97.6 270.4H107.2V280H97.6V270.4ZM116.8 270.4H126.4V280H116.8V270.4ZM155.2 270.4H164.8V280H155.2V270.4ZM174.4 270.4H184V280H174.4V270.4ZM222.4 270.4H232V280H222.4V270.4ZM232 270.4H241.6V280H232V270.4ZM241.6 270.4H251.2V280H241.6V270.4ZM251.2 270.4H260.8V280H251.2V270.4ZM260.8 270.4H270.4V280H260.8V270.4ZM270.4 270.4H280V280H270.4V270.4Z"
                fill="#FFFFFE"
              ></path>
              <path
                d="M64 4H44C21.9086 4 4 21.9086 4 44V64"
                stroke="#95D1F9"
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M64 315H44C21.9086 315 4 297.091 4 275V255"
                stroke="#95D1F9"
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M256 4H276C298.091 4 316 21.9086 316 44V64"
                stroke="#95D1F9"
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M256 316H276C298.091 316 316 298.091 316 276V256"
                stroke="#95D1F9"
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
          </figure>
          <figure className="lg:d-none md:pb-2 md:pl-8 md:pt-5">
            <svg
              width="161"
              height="160"
              viewBox="0 0 161 160"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0)">
                <path
                  d="M20.5 20H25.3V24.8H20.5V20ZM25.3 20H30.1V24.8H25.3V20ZM30.1 20H34.9V24.8H30.1V20ZM34.9 20H39.7V24.8H34.9V20ZM39.7 20H44.5V24.8H39.7V20ZM44.5 20H49.3V24.8H44.5V20ZM49.3 20H54.1V24.8H49.3V20ZM63.7 20H68.5V24.8H63.7V20ZM87.7 20H92.5V24.8H87.7V20ZM92.5 20H97.3V24.8H92.5V20ZM97.3 20H102.1V24.8H97.3V20ZM106.9 20H111.7V24.8H106.9V20ZM111.7 20H116.5V24.8H111.7V20ZM116.5 20H121.3V24.8H116.5V20ZM121.3 20H126.1V24.8H121.3V20ZM126.1 20H130.9V24.8H126.1V20ZM130.9 20H135.7V24.8H130.9V20ZM135.7 20H140.5V24.8H135.7V20ZM20.5 24.8H25.3V29.6H20.5V24.8ZM49.3 24.8H54.1V29.6H49.3V24.8ZM58.9 24.8H63.7V29.6H58.9V24.8ZM63.7 24.8H68.5V29.6H63.7V24.8ZM73.3 24.8H78.1V29.6H73.3V24.8ZM78.1 24.8H82.9V29.6H78.1V24.8ZM82.9 24.8H87.7V29.6H82.9V24.8ZM87.7 24.8H92.5V29.6H87.7V24.8ZM92.5 24.8H97.3V29.6H92.5V24.8ZM106.9 24.8H111.7V29.6H106.9V24.8ZM135.7 24.8H140.5V29.6H135.7V24.8ZM20.5 29.6H25.3V34.4H20.5V29.6ZM30.1 29.6H34.9V34.4H30.1V29.6ZM34.9 29.6H39.7V34.4H34.9V29.6ZM39.7 29.6H44.5V34.4H39.7V29.6ZM49.3 29.6H54.1V34.4H49.3V29.6ZM63.7 29.6H68.5V34.4H63.7V29.6ZM68.5 29.6H73.3V34.4H68.5V29.6ZM73.3 29.6H78.1V34.4H73.3V29.6ZM92.5 29.6H97.3V34.4H92.5V29.6ZM97.3 29.6H102.1V34.4H97.3V29.6ZM106.9 29.6H111.7V34.4H106.9V29.6ZM116.5 29.6H121.3V34.4H116.5V29.6ZM121.3 29.6H126.1V34.4H121.3V29.6ZM126.1 29.6H130.9V34.4H126.1V29.6ZM135.7 29.6H140.5V34.4H135.7V29.6ZM20.5 34.4H25.3V39.2H20.5V34.4ZM30.1 34.4H34.9V39.2H30.1V34.4ZM34.9 34.4H39.7V39.2H34.9V34.4ZM39.7 34.4H44.5V39.2H39.7V34.4ZM49.3 34.4H54.1V39.2H49.3V34.4ZM58.9 34.4H63.7V39.2H58.9V34.4ZM63.7 34.4H68.5V39.2H63.7V34.4ZM73.3 34.4H78.1V39.2H73.3V34.4ZM82.9 34.4H87.7V39.2H82.9V34.4ZM87.7 34.4H92.5V39.2H87.7V34.4ZM92.5 34.4H97.3V39.2H92.5V34.4ZM97.3 34.4H102.1V39.2H97.3V34.4ZM106.9 34.4H111.7V39.2H106.9V34.4ZM116.5 34.4H121.3V39.2H116.5V34.4ZM121.3 34.4H126.1V39.2H121.3V34.4ZM126.1 34.4H130.9V39.2H126.1V34.4ZM135.7 34.4H140.5V39.2H135.7V34.4ZM20.5 39.2H25.3V44H20.5V39.2ZM30.1 39.2H34.9V44H30.1V39.2ZM34.9 39.2H39.7V44H34.9V39.2ZM39.7 39.2H44.5V44H39.7V39.2ZM49.3 39.2H54.1V44H49.3V39.2ZM68.5 39.2H73.3V44H68.5V39.2ZM78.1 39.2H82.9V44H78.1V39.2ZM82.9 39.2H87.7V44H82.9V39.2ZM92.5 39.2H97.3V44H92.5V39.2ZM97.3 39.2H102.1V44H97.3V39.2ZM106.9 39.2H111.7V44H106.9V39.2ZM116.5 39.2H121.3V44H116.5V39.2ZM121.3 39.2H126.1V44H121.3V39.2ZM126.1 39.2H130.9V44H126.1V39.2ZM135.7 39.2H140.5V44H135.7V39.2ZM20.5 44H25.3V48.8H20.5V44ZM49.3 44H54.1V48.8H49.3V44ZM58.9 44H63.7V48.8H58.9V44ZM68.5 44H73.3V48.8H68.5V44ZM82.9 44H87.7V48.8H82.9V44ZM87.7 44H92.5V48.8H87.7V44ZM92.5 44H97.3V48.8H92.5V44ZM106.9 44H111.7V48.8H106.9V44ZM135.7 44H140.5V48.8H135.7V44ZM20.5 48.8H25.3V53.6H20.5V48.8ZM25.3 48.8H30.1V53.6H25.3V48.8ZM30.1 48.8H34.9V53.6H30.1V48.8ZM34.9 48.8H39.7V53.6H34.9V48.8ZM39.7 48.8H44.5V53.6H39.7V48.8ZM44.5 48.8H49.3V53.6H44.5V48.8ZM49.3 48.8H54.1V53.6H49.3V48.8ZM58.9 48.8H63.7V53.6H58.9V48.8ZM68.5 48.8H73.3V53.6H68.5V48.8ZM78.1 48.8H82.9V53.6H78.1V48.8ZM87.7 48.8H92.5V53.6H87.7V48.8ZM97.3 48.8H102.1V53.6H97.3V48.8ZM106.9 48.8H111.7V53.6H106.9V48.8ZM111.7 48.8H116.5V53.6H111.7V48.8ZM116.5 48.8H121.3V53.6H116.5V48.8ZM121.3 48.8H126.1V53.6H121.3V48.8ZM126.1 48.8H130.9V53.6H126.1V48.8ZM130.9 48.8H135.7V53.6H130.9V48.8ZM135.7 48.8H140.5V53.6H135.7V48.8ZM78.1 53.6H82.9V58.4H78.1V53.6ZM87.7 53.6H92.5V58.4H87.7V53.6ZM20.5 58.4H25.3V63.2H20.5V58.4ZM25.3 58.4H30.1V63.2H25.3V58.4ZM30.1 58.4H34.9V63.2H30.1V58.4ZM34.9 58.4H39.7V63.2H34.9V58.4ZM39.7 58.4H44.5V63.2H39.7V58.4ZM49.3 58.4H54.1V63.2H49.3V58.4ZM54.1 58.4H58.9V63.2H54.1V58.4ZM58.9 58.4H63.7V63.2H58.9V58.4ZM63.7 58.4H68.5V63.2H63.7V58.4ZM68.5 58.4H73.3V63.2H68.5V58.4ZM78.1 58.4H82.9V63.2H78.1V58.4ZM82.9 58.4H87.7V63.2H82.9V58.4ZM87.7 58.4H92.5V63.2H87.7V58.4ZM92.5 58.4H97.3V63.2H92.5V58.4ZM97.3 58.4H102.1V63.2H97.3V58.4ZM102.1 58.4H106.9V63.2H102.1V58.4ZM111.7 58.4H116.5V63.2H111.7V58.4ZM121.3 58.4H126.1V63.2H121.3V58.4ZM130.9 58.4H135.7V63.2H130.9V58.4ZM20.5 63.2H25.3V68H20.5V63.2ZM25.3 63.2H30.1V68H25.3V63.2ZM30.1 63.2H34.9V68H30.1V63.2ZM34.9 63.2H39.7V68H34.9V63.2ZM44.5 63.2H49.3V68H44.5V63.2ZM58.9 63.2H63.7V68H58.9V63.2ZM63.7 63.2H68.5V68H63.7V63.2ZM82.9 63.2H87.7V68H82.9V63.2ZM111.7 63.2H116.5V68H111.7V63.2ZM130.9 63.2H135.7V68H130.9V63.2ZM20.5 68H25.3V72.8H20.5V68ZM34.9 68H39.7V72.8H34.9V68ZM39.7 68H44.5V72.8H39.7V68ZM49.3 68H54.1V72.8H49.3V68ZM54.1 68H58.9V72.8H54.1V68ZM58.9 68H63.7V72.8H58.9V68ZM63.7 68H68.5V72.8H63.7V68ZM73.3 68H78.1V72.8H73.3V68ZM78.1 68H82.9V72.8H78.1V68ZM82.9 68H87.7V72.8H82.9V68ZM87.7 68H92.5V72.8H87.7V68ZM92.5 68H97.3V72.8H92.5V68ZM97.3 68H102.1V72.8H97.3V68ZM116.5 68H121.3V72.8H116.5V68ZM121.3 68H126.1V72.8H121.3V68ZM130.9 68H135.7V72.8H130.9V68ZM135.7 68H140.5V72.8H135.7V68ZM25.3 72.8H30.1V77.6H25.3V72.8ZM30.1 72.8H34.9V77.6H30.1V72.8ZM44.5 72.8H49.3V77.6H44.5V72.8ZM63.7 72.8H68.5V77.6H63.7V72.8ZM68.5 72.8H73.3V77.6H68.5V72.8ZM73.3 72.8H78.1V77.6H73.3V72.8ZM97.3 72.8H102.1V77.6H97.3V72.8ZM102.1 72.8H106.9V77.6H102.1V72.8ZM106.9 72.8H111.7V77.6H106.9V72.8ZM135.7 72.8H140.5V77.6H135.7V72.8ZM20.5 77.6H25.3V82.4H20.5V77.6ZM30.1 77.6H34.9V82.4H30.1V77.6ZM39.7 77.6H44.5V82.4H39.7V77.6ZM49.3 77.6H54.1V82.4H49.3V77.6ZM58.9 77.6H63.7V82.4H58.9V77.6ZM63.7 77.6H68.5V82.4H63.7V77.6ZM73.3 77.6H78.1V82.4H73.3V77.6ZM87.7 77.6H92.5V82.4H87.7V77.6ZM102.1 77.6H106.9V82.4H102.1V77.6ZM106.9 77.6H111.7V82.4H106.9V77.6ZM116.5 77.6H121.3V82.4H116.5V77.6ZM126.1 77.6H130.9V82.4H126.1V77.6ZM130.9 77.6H135.7V82.4H130.9V77.6ZM135.7 77.6H140.5V82.4H135.7V77.6ZM20.5 82.4H25.3V87.2H20.5V82.4ZM25.3 82.4H30.1V87.2H25.3V82.4ZM63.7 82.4H68.5V87.2H63.7V82.4ZM68.5 82.4H73.3V87.2H68.5V82.4ZM78.1 82.4H82.9V87.2H78.1V82.4ZM87.7 82.4H92.5V87.2H87.7V82.4ZM102.1 82.4H106.9V87.2H102.1V82.4ZM111.7 82.4H116.5V87.2H111.7V82.4ZM121.3 82.4H126.1V87.2H121.3V82.4ZM130.9 82.4H135.7V87.2H130.9V82.4ZM20.5 87.2H25.3V92H20.5V87.2ZM30.1 87.2H34.9V92H30.1V87.2ZM34.9 87.2H39.7V92H34.9V87.2ZM44.5 87.2H49.3V92H44.5V87.2ZM49.3 87.2H54.1V92H49.3V87.2ZM82.9 87.2H87.7V92H82.9V87.2ZM87.7 87.2H92.5V92H87.7V87.2ZM92.5 87.2H97.3V92H92.5V87.2ZM97.3 87.2H102.1V92H97.3V87.2ZM102.1 87.2H106.9V92H102.1V87.2ZM106.9 87.2H111.7V92H106.9V87.2ZM111.7 87.2H116.5V92H111.7V87.2ZM116.5 87.2H121.3V92H116.5V87.2ZM121.3 87.2H126.1V92H121.3V87.2ZM130.9 87.2H135.7V92H130.9V87.2ZM135.7 87.2H140.5V92H135.7V87.2ZM20.5 92H25.3V96.8H20.5V92ZM34.9 92H39.7V96.8H34.9V92ZM54.1 92H58.9V96.8H54.1V92ZM58.9 92H63.7V96.8H58.9V92ZM63.7 92H68.5V96.8H63.7V92ZM78.1 92H82.9V96.8H78.1V92ZM92.5 92H97.3V96.8H92.5V92ZM106.9 92H111.7V96.8H106.9V92ZM111.7 92H116.5V96.8H111.7V92ZM116.5 92H121.3V96.8H116.5V92ZM135.7 92H140.5V96.8H135.7V92ZM20.5 96.8H25.3V101.6H20.5V96.8ZM34.9 96.8H39.7V101.6H34.9V96.8ZM49.3 96.8H54.1V101.6H49.3V96.8ZM54.1 96.8H58.9V101.6H54.1V96.8ZM63.7 96.8H68.5V101.6H63.7V96.8ZM78.1 96.8H82.9V101.6H78.1V96.8ZM82.9 96.8H87.7V101.6H82.9V96.8ZM97.3 96.8H102.1V101.6H97.3V96.8ZM102.1 96.8H106.9V101.6H102.1V96.8ZM106.9 96.8H111.7V101.6H106.9V96.8ZM111.7 96.8H116.5V101.6H111.7V96.8ZM116.5 96.8H121.3V101.6H116.5V96.8ZM126.1 96.8H130.9V101.6H126.1V96.8ZM58.9 101.6H63.7V106.4H58.9V101.6ZM82.9 101.6H87.7V106.4H82.9V101.6ZM87.7 101.6H92.5V106.4H87.7V101.6ZM97.3 101.6H102.1V106.4H97.3V101.6ZM116.5 101.6H121.3V106.4H116.5V101.6ZM121.3 101.6H126.1V106.4H121.3V101.6ZM20.5 106.4H25.3V111.2H20.5V106.4ZM25.3 106.4H30.1V111.2H25.3V106.4ZM30.1 106.4H34.9V111.2H30.1V106.4ZM34.9 106.4H39.7V111.2H34.9V106.4ZM39.7 106.4H44.5V111.2H39.7V106.4ZM44.5 106.4H49.3V111.2H44.5V106.4ZM49.3 106.4H54.1V111.2H49.3V106.4ZM58.9 106.4H63.7V111.2H58.9V106.4ZM63.7 106.4H68.5V111.2H63.7V106.4ZM73.3 106.4H78.1V111.2H73.3V106.4ZM78.1 106.4H82.9V111.2H78.1V106.4ZM97.3 106.4H102.1V111.2H97.3V106.4ZM106.9 106.4H111.7V111.2H106.9V106.4ZM116.5 106.4H121.3V111.2H116.5V106.4ZM126.1 106.4H130.9V111.2H126.1V106.4ZM130.9 106.4H135.7V111.2H130.9V106.4ZM135.7 106.4H140.5V111.2H135.7V106.4ZM20.5 111.2H25.3V116H20.5V111.2ZM49.3 111.2H54.1V116H49.3V111.2ZM63.7 111.2H68.5V116H63.7V111.2ZM73.3 111.2H78.1V116H73.3V111.2ZM97.3 111.2H102.1V116H97.3V111.2ZM116.5 111.2H121.3V116H116.5V111.2ZM121.3 111.2H126.1V116H121.3V111.2ZM20.5 116H25.3V120.8H20.5V116ZM30.1 116H34.9V120.8H30.1V116ZM34.9 116H39.7V120.8H34.9V116ZM39.7 116H44.5V120.8H39.7V116ZM49.3 116H54.1V120.8H49.3V116ZM58.9 116H63.7V120.8H58.9V116ZM63.7 116H68.5V120.8H63.7V116ZM73.3 116H78.1V120.8H73.3V116ZM92.5 116H97.3V120.8H92.5V116ZM97.3 116H102.1V120.8H97.3V116ZM102.1 116H106.9V120.8H102.1V116ZM106.9 116H111.7V120.8H106.9V116ZM111.7 116H116.5V120.8H111.7V116ZM116.5 116H121.3V120.8H116.5V116ZM126.1 116H130.9V120.8H126.1V116ZM130.9 116H135.7V120.8H130.9V116ZM135.7 116H140.5V120.8H135.7V116ZM20.5 120.8H25.3V125.6H20.5V120.8ZM30.1 120.8H34.9V125.6H30.1V120.8ZM34.9 120.8H39.7V125.6H34.9V120.8ZM39.7 120.8H44.5V125.6H39.7V120.8ZM49.3 120.8H54.1V125.6H49.3V120.8ZM58.9 120.8H63.7V125.6H58.9V120.8ZM63.7 120.8H68.5V125.6H63.7V120.8ZM68.5 120.8H73.3V125.6H68.5V120.8ZM78.1 120.8H82.9V125.6H78.1V120.8ZM82.9 120.8H87.7V125.6H82.9V120.8ZM92.5 120.8H97.3V125.6H92.5V120.8ZM106.9 120.8H111.7V125.6H106.9V120.8ZM116.5 120.8H121.3V125.6H116.5V120.8ZM121.3 120.8H126.1V125.6H121.3V120.8ZM126.1 120.8H130.9V125.6H126.1V120.8ZM130.9 120.8H135.7V125.6H130.9V120.8ZM135.7 120.8H140.5V125.6H135.7V120.8ZM20.5 125.6H25.3V130.4H20.5V125.6ZM30.1 125.6H34.9V130.4H30.1V125.6ZM34.9 125.6H39.7V130.4H34.9V125.6ZM39.7 125.6H44.5V130.4H39.7V125.6ZM49.3 125.6H54.1V130.4H49.3V125.6ZM58.9 125.6H63.7V130.4H58.9V125.6ZM63.7 125.6H68.5V130.4H63.7V125.6ZM68.5 125.6H73.3V130.4H68.5V125.6ZM82.9 125.6H87.7V130.4H82.9V125.6ZM87.7 125.6H92.5V130.4H87.7V125.6ZM97.3 125.6H102.1V130.4H97.3V125.6ZM121.3 125.6H126.1V130.4H121.3V125.6ZM126.1 125.6H130.9V130.4H126.1V125.6ZM135.7 125.6H140.5V130.4H135.7V125.6ZM20.5 130.4H25.3V135.2H20.5V130.4ZM49.3 130.4H54.1V135.2H49.3V130.4ZM58.9 130.4H63.7V135.2H58.9V130.4ZM63.7 130.4H68.5V135.2H63.7V130.4ZM68.5 130.4H73.3V135.2H68.5V130.4ZM78.1 130.4H82.9V135.2H78.1V130.4ZM82.9 130.4H87.7V135.2H82.9V130.4ZM92.5 130.4H97.3V135.2H92.5V130.4ZM97.3 130.4H102.1V135.2H97.3V130.4ZM102.1 130.4H106.9V135.2H102.1V130.4ZM111.7 130.4H116.5V135.2H111.7V130.4ZM116.5 130.4H121.3V135.2H116.5V130.4ZM121.3 130.4H126.1V135.2H121.3V130.4ZM135.7 130.4H140.5V135.2H135.7V130.4ZM20.5 135.2H25.3V140H20.5V135.2ZM25.3 135.2H30.1V140H25.3V135.2ZM30.1 135.2H34.9V140H30.1V135.2ZM34.9 135.2H39.7V140H34.9V135.2ZM39.7 135.2H44.5V140H39.7V135.2ZM44.5 135.2H49.3V140H44.5V135.2ZM49.3 135.2H54.1V140H49.3V135.2ZM58.9 135.2H63.7V140H58.9V135.2ZM78.1 135.2H82.9V140H78.1V135.2ZM87.7 135.2H92.5V140H87.7V135.2ZM111.7 135.2H116.5V140H111.7V135.2ZM116.5 135.2H121.3V140H116.5V135.2ZM121.3 135.2H126.1V140H121.3V135.2ZM126.1 135.2H130.9V140H126.1V135.2ZM130.9 135.2H135.7V140H130.9V135.2ZM135.7 135.2H140.5V140H135.7V135.2Z"
                  fill="#FFFFFE"
                ></path>
              </g>
              <path
                d="M32.5 2H22.5C11.4543 2 2.5 10.9543 2.5 22V32"
                stroke="#95D1F9"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M32.5 157.5H22.5C11.4543 157.5 2.5 148.546 2.5 137.5V127.5"
                stroke="#95D1F9"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M128.5 2H138.5C149.546 2 158.5 10.9543 158.5 22V32"
                stroke="#95D1F9"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M128.5 158H138.5C149.546 158 158.5 149.046 158.5 138V128"
                stroke="#95D1F9"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <defs>
                <clipPath id="clip0">
                  <rect
                    width="120"
                    height="120"
                    fill="white"
                    transform="translate(20.5 20)"
                  ></rect>
                </clipPath>
              </defs>
            </svg>
          </figure>
        </div>
      </section>
    );
  };
  const SlideShow = ({ data }) => {
    return (
      <div className="slideshow-container">
        <div className="image-slides fade d-block">
          <ResponsiveImage
            loading="lazy"
            alt={data[0].alt}
            src={data[0].src}
            dpi={data[0].prefixes.dpi}
            imgClass="img-fit"
            wrapClass="lg:maxw-lg"
            hasMobileImg={false}
          />
        </div>
        <div className="image-slides fade">
          <ResponsiveImage
            loading="lazy"
            alt={data[1].alt}
            src={data[1].src}
            dpi={data[1].prefixes.dpi}
            imgClass="img-fit"
            wrapClass="lg:maxw-lg"
            hasMobileImg={false}
          />
        </div>
        <div className="image-slides fade">
          <ResponsiveImage
            loading="lazy"
            alt={data[2].alt}
            src={data[2].src}
            dpi={data[2].prefixes.dpi}
            imgClass="img-fit"
            wrapClass="lg:maxw-lg"
            hasMobileImg={false}
          />
        </div>
        <div className="image-slides-dot" id="image-slides">
          <span
            className="image-dot active"
            onClick={() => {
              setSlide(1);
            }}
          ></span>
          <span
            className="image-dot"
            onClick={() => {
              setSlide(2);
            }}
          ></span>
          <span
            className="image-dot"
            onClick={() => {
              setSlide(3);
            }}
          ></span>
        </div>
      </div>
    );
  };
  const ComplexSecondFold = ({ data }) => {
    const appMode = process.env.APP_MODE;
    const link =
      data?.featuredImageThree?.cta?.href?.[appMode] ||
      data?.featuredImageThree?.cta?.href;
    return (
      <section className="bg-light lg:pt-10 md:pt-5">
        <div className="container grid--default has-align-middle lg:pb-15 md:pb-10">
          <ResponsiveImage
            loading="lazy"
            alt={data.image.alt}
            src={data.image.src}
            dpi={data.image.prefixes.dpi}
            imgClass="img-fit"
            wrapClass="lg:maxw-lg"
            hasMobileImg={false}
          />{" "}
          <article className="text-left lg:maxw-md lg:mt-5">
            <h2
              className="text-lg lg:mb-3 md:mb-2 md:mt-5"
              dangerouslySetInnerHTML={{ __html: data.heading }}
            />
            <p
              className="lg:mb-5"
              dangerouslySetInnerHTML={{ __html: data.desc }}
            />
          </article>
        </div>
        <div className="container grid--default has-align-middle lg:pb-15 md:pb-10 md:fx md:fx--w">
          <article className="text-left lg:maxw-md fx--o-2">
            <h2
              className="text-lg lg:mb-2.5 md:mb-2 md:mt-4"
              dangerouslySetInnerHTML={{
                __html: data.featuredImageOne.heading,
              }}
            />
            <p
              className="lg:mb-5"
              dangerouslySetInnerHTML={{
                __html: data.featuredImageOne.desc,
              }}
            />
          </article>{" "}
          <SlideShow data={data.slideImages} />
        </div>
        <div className="container grid--default has-align-middle">
          <ResponsiveImage
            loading="lazy"
            alt={data.featuredImageTwo.image.alt}
            src={data.featuredImageTwo.image.src}
            dpi={data.featuredImageTwo.image.prefixes.dpi}
            imgClass="img-fit"
            wrapClass="lg:maxw-lg"
            hasMobileImg={false}
          />{" "}
          <article className="text-left lg:maxw-md lg:mt-5">
            <h2
              className="text-lg lg:mb-3 md:mb-2 md:mt-5"
              dangerouslySetInnerHTML={{
                __html: data.featuredImageTwo.heading,
              }}
            />
            <p
              className="lg:mb-5"
              dangerouslySetInnerHTML={{
                __html: data.featuredImageTwo.desc,
              }}
            />
          </article>
        </div>
        <CTA data={data.CTA} />
        <div className="container grid--default has-align-middle lg:pt-20 lg:pb-15 md:pb-7 md:pt-10">
          <ResponsiveImage
            loading="lazy"
            alt={data.featuredImageThree.image.alt}
            src={data.featuredImageThree.image.src}
            dpi={data.featuredImageThree.image.prefixes.dpi}
            imgClass="lg:-ml-12 lg:w-auto img-fit"
            wrapClass="lg:maxw-lg"
            hasMobileImg={false}
          />{" "}
          <article className="text-left lg:maxw-md">
            <h2
              className="text-lg lg:pr-5 lg:mb-2.5 md:mb-2 md:mt-5"
              dangerouslySetInnerHTML={{
                __html: data.featuredImageThree.heading,
              }}
            />
            <p
              className="lg:mb-5"
              dangerouslySetInnerHTML={{
                __html: data.featuredImageThree.desc,
              }}
            />
            <p className="md:text-center">
              <a
                href={link}
                className="btn-primary md:mb-3 md:mt-5 md:w-100"
                id={data.featuredImageThree.cta.id}
                data-aos="fade-up"
              >
                <span>{data.featuredImageThree.cta.text}</span>
              </a>
            </p>
          </article>
        </div>
        <div className="lg:pb-15 md:pb-7">
          <section className="customise-wrap has-content-left container grid--wrap br-8 bg-white">
            <article className="lg:maxw-md text-left md:fx--o-1">
              <h2
                className="text-lg"
                dangerouslySetInnerHTML={{
                  __html: data.CTASecondary.heading,
                }}
              />
              <p
                dangerouslySetInnerHTML={{
                  __html: data.CTASecondary.desc,
                }}
              />
              <a
                href={data.CTASecondary.cta.href}
                className="btn-primary mt-5 md:w-100"
              >
                <span>{data.CTASecondary.cta.text}</span>
              </a>
            </article>{" "}
            <ResponsiveImage
              loading="lazy"
              alt={data.CTASecondary.image.alt}
              src={data.CTASecondary.image.src}
              dpi={data.CTASecondary.image.prefixes.dpi}
              mobile={data.CTASecondary.image.prefixes.mobile}
            />
          </section>
        </div>
      </section>
    );
  };
  const BottomCards = ({ data }) => {
    return (
      <section className="lg:pb-15 lg:pt-3 md:pt-10 md:pb-10 features bg-offset-top">
        <article className="container lg:maxw-xl block--align-center lg:text-center md:text-left">
          <h2
            className="text-xl md:text-mds"
            dangerouslySetInnerHTML={{
              __html: data.heading,
            }}
          />
          <p
            className="mx-auto lg:mb-10 lg:text-center lg:maxw-xl"
            dangerouslySetInnerHTML={{
              __html: data.desc,
            }}
          />
        </article>
        <FeaturesGridWithLink data={data.featuresShowcaseCards} />
      </section>
    );
  };
  return (
    <>
      <Theme
        header={header}
        footer={footer}
        className="integration-listing-page land-pages cls-improvement--desktop qr-landing-page"
        seo={seo}
        nonce={nonce}
      >
        <AppStorage pageName="Features Sub Page - qr-code-appointment-booking" />
        <Hero data={content.hero} />
        <ComplexSecondFold data={content.secondFold} />
        <BottomCards data={content.highlightCards} />
        {content.hasOwnProperty("faqs") && (
          <>
            <FAQ className="lg:mb-15 md:mb-10" data={content.faqs} />
            <Schema data={content.faqs.list} />
          </>
        )}
        <People />
        <WideCTA data={content.wideCTA} />
      </Theme>
    </>
  );
}
